// B3 — prerequisite debt (PRD 16 §5). *"Your Getting Started page requires four
// levels of undefined prior knowledge."*
//
// The cheapest of the three Tier-B views: `prereq_dag` has been written on every
// reconcile since Pass 2 shipped and had zero consumers. This is the first one.
//
// Four things here are deliberate and should not be "tidied":
//
//  1. **A depth of 0 is a real answer, not a missing one.** It means the concept
//     assumes nothing, which is the healthy case. It never renders as
//     NOT_MEASURED — only a prerequisite LOOP does that, because a loop has no
//     bottom to count to.
//  2. **A capped chain reads "4 or more".** Depth is bounded at `depth_cap`, so
//     printing a bare "4" would tie a document that is genuinely six deep with
//     one that is exactly four — flattening the worst case this view exists to
//     find.
//  3. **The two kinds of missing definition are rendered separately.** "Nothing
//     in your corpus defines this" and "another page defines this" are different
//     faults with different fixes; a single "undefined" count would hide both.
//  4. **The un-reconciled corpus is its own state**, for the reason
//     `ImpactRadius` spells out: "nothing assumes prior knowledge" and
//     "reconciliation has not run" are opposite claims.
//
// No session, visit or dwell number appears anywhere in this panel. It is
// computed from corpus text, which is exactly why it reaches a buyer the
// look-up leaderboard never does — so nothing here carries a rate.

import { Card, EmptyState, MeterBar } from "../../ui";
import type { PrereqDebtReport, PrereqRow } from "./api";
import { shownOf } from "./format";
import {
  PREREQ_ASSUMES_NOTHING,
  PREREQ_DEPTH_WHY,
  PREREQ_EMPTY_BODY,
  PREREQ_EMPTY_HEADLINE,
  PREREQ_LEVELS,
  PREREQ_NO_DEPTH_WHY,
  PREREQ_OFF_DOC_LABEL,
  PREREQ_OFF_DOC_WHY,
  PREREQ_OR_MORE,
  PREREQ_SUBTITLE,
  PREREQ_TITLE,
  PREREQ_UNRECONCILED_BODY,
  PREREQ_UNRECONCILED_HEADLINE,
  PREREQ_UNRESOLVED_LABEL,
  PREREQ_UNRESOLVED_WHY,
} from "./metricLabels";
import "./analytics.css";

/** A work-list, not an inventory — same reasoning as `corpus/health.py`'s
 *  TOP_N. The total is always stated beside it. */
const MAX_ROWS = 5;
const MAX_CHIPS = 4;

/** "3 levels deep", "4 or more levels deep", "assumes nothing". */
export function depthLabel(row: PrereqRow): string {
  if (row.depth === null) return "";
  if (row.depth === 0) return PREREQ_ASSUMES_NOTHING;
  return row.depth_capped
    ? `${row.depth} ${PREREQ_OR_MORE} ${PREREQ_LEVELS}`
    : `${row.depth} ${PREREQ_LEVELS}`;
}

function names(list: string[]): string {
  return list.length > MAX_CHIPS
    ? `${list.slice(0, MAX_CHIPS).join(", ")}, +${list.length - MAX_CHIPS} more`
    : list.join(", ");
}

export function PrerequisiteDebt({ report }: { report: PrereqDebtReport }) {
  const shown = report.rows.slice(0, MAX_ROWS);
  const where = report.doc_id ?? "";

  return (
    <Card
      header={PREREQ_TITLE}
      footer={
        <span className="type-bodyS">
          {report.reconciled && report.concepts_found > 0
            ? `${shownOf(shown.length, report.concepts_found, "concept")} on ${where} · ${report.unresolved_total} ${PREREQ_UNRESOLVED_LABEL} · ${report.off_document_total} ${PREREQ_OFF_DOC_LABEL}`
            : PREREQ_SUBTITLE}
        </span>
      }
    >
      {!report.reconciled ? (
        <EmptyState
          headline={PREREQ_UNRECONCILED_HEADLINE}
          body={PREREQ_UNRECONCILED_BODY}
        />
      ) : report.concepts_with_prereqs === 0 ? (
        <EmptyState headline={PREREQ_EMPTY_HEADLINE} body={PREREQ_EMPTY_BODY} />
      ) : (
        <ul
          className="maisight-analytics-meters"
          aria-label="Prerequisite depth by concept"
        >
          {shown.map((row) => {
            const measured = row.depth !== null;
            return (
              <li key={row.entity_id} className="maisight-analytics-meter-row">
                <div className="maisight-analytics-meter-head">
                  <span className="maisight-analytics-term type-numSm">
                    {row.term}
                  </span>
                  <span
                    className="maisight-analytics-metric type-numSm"
                    title={measured ? PREREQ_DEPTH_WHY : PREREQ_NO_DEPTH_WHY}
                    data-measured={measured ? "yes" : "no"}
                  >
                    {measured ? depthLabel(row) : "no depth — prerequisites loop"}
                  </span>
                </div>
                {/* No bar for a loop. A meter at zero would read as "assumes
                  *  nothing", which is the opposite of what a loop means. */}
                {measured ? (
                  <MeterBar
                    value={row.depth ?? 0}
                    max={report.depth_cap}
                    tone={
                      (row.depth ?? 0) >= report.depth_cap ? "flagged" : "nominal"
                    }
                    label={
                      row.depth === 0
                        ? `${row.term} assumes no prior knowledge`
                        : `${row.term} sits ${depthLabel(row)}`
                    }
                  />
                ) : null}
                <p className="maisight-analytics-prereq-meta type-bodyS">
                  {row.unresolved_prereqs.length > 0 ? (
                    <span
                      className="maisight-analytics-prereq-unresolved"
                      title={PREREQ_UNRESOLVED_WHY}
                    >
                      {`${PREREQ_UNRESOLVED_LABEL}: ${names(row.unresolved_prereqs)}`}
                    </span>
                  ) : null}
                  {row.off_document_prereqs.length > 0 ? (
                    <span title={PREREQ_OFF_DOC_WHY}>
                      {`${PREREQ_OFF_DOC_LABEL}: ${names(row.off_document_prereqs)}`}
                    </span>
                  ) : null}
                  {!measured ? <span>{row.not_measured_reason}</span> : null}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
